import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAnimatedValueOptions {
  duration?: number;
  precision?: number;
  enabled?: boolean;
  min?: number;  // Minimum allowed value (prevents negative)
}

/**
 * Hook for smooth number animations using requestAnimationFrame
 * @param targetValue - The target number to animate towards
 * @param options - Animation configuration
 * @returns The current animated value
 */
export function useAnimatedValue(
  targetValue: number,
  options: UseAnimatedValueOptions = {}
): number {
  const { duration = 800, precision = 0, enabled = true, min = 0 } = options;
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef<number | undefined>(undefined);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(0);
  const currentTargetRef = useRef(targetValue);

  // Track the current display value for animation start without dependency
  const displayValueRef = useRef(displayValue);
  displayValueRef.current = displayValue;

  const animate = useCallback((currentTime: number) => {
    const elapsed = currentTime - startTimeRef.current;
    const progress = Math.min(elapsed / duration, 1);

    // Ease-out cubic for smooth deceleration
    const easeOut = 1 - Math.pow(1 - progress, 3);

    const newValue =
      startValueRef.current +
      (currentTargetRef.current - startValueRef.current) * easeOut;

    // Clamp to minimum value to prevent negatives
    const clampedValue = Math.max(min, Number(newValue.toFixed(precision)));
    setDisplayValue(clampedValue);

    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [duration, precision, min]);

  useEffect(() => {
    // If animations disabled, just set the value directly
    if (!enabled) {
      setDisplayValue(Math.max(min, targetValue));
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Skip animation if values are essentially the same
    if (Math.abs(displayValueRef.current - targetValue) < 0.001) {
      return;
    }

    // Update refs for new animation
    currentTargetRef.current = targetValue;
    startValueRef.current = displayValueRef.current;
    startTimeRef.current = performance.now();

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, precision, enabled, min, animate]);

  return displayValue;
}
